import React from 'react';
import { 
  Card, 
  List, 
  ListItem, 
  ListItemAvatar, 
  ListItemText, 
  ListItemSecondaryAction,
  Avatar, 
  Divider,
  IconButton,
  Tooltip,
  Box,
  Typography,
  Chip
} from '@mui/material';
import { 
  Description, 
  Image, 
  VideoLibrary, 
  Folder,
  Delete as DeleteIcon,
  InsertDriveFile,
  Article, 
  Slideshow, 
  TableChart, 
  Archive
} from '@mui/icons-material';
import { useTheme } from '@emotion/react';
import { tokens } from '../theme';
import { alpha } from '@mui/material/styles';

export default function FileList({ 
  files, 
  showDelete = false, 
  onDelete
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const iconMap = {
    pdf: <Description fontSize="small" />,
    image: <Image fontSize="small" />,
    video: <VideoLibrary fontSize="small" />,
    folder: <Folder fontSize="small" />,
    docx: <Article fontSize="small" />,
    pptx: <Slideshow fontSize="small" />,
    xlsx: <TableChart fontSize="small" />,
    zip: <Archive fontSize="small" />,
    default: <InsertDriveFile fontSize="small" />
  };
  
  const getTypeColor = (type) => {
    switch(type) {
      case 'pdf': return colors.redAccent[400];
      case 'image': return colors.Blue[400];
      case 'video': return colors.purple[400];
      case 'folder': return colors.Orange[400];
      case 'docx': return colors.Blue[400];
      case 'pptx': return colors.Orange[400];
      case 'xlsx': return colors.green[400];
      case 'zip': return colors.grey[500];
      default: return colors.grey[500];
    }
  };

  const getIcon = (type) => iconMap[type] || iconMap.default;

  const handleDelete = (file) => {
    if (onDelete) {
      onDelete(file);
    }
  };

  return (
    <Card 
      elevation={0} 
      sx={{ 
        width: '100%',
        border: `1px solid ${alpha(colors.grey[300], 0.3)}`,
        borderRadius: '12px',
        overflow: 'hidden'
      }}
    >
      <List sx={{ p: 0 }}>
        {files.map((file, index) => (
          <React.Fragment key={index}>
            <ListItem 
              sx={{ 
                py: 0.5,
                px: 1,
                '&:hover': { 
                  backgroundColor: alpha(colors.primary[100], 0.05) 
                }
              }}
            >
              <ListItemAvatar>
                <Avatar 
                  sx={{ 
                    bgcolor: alpha(getTypeColor(file.type), 0.1),
                    color: getTypeColor(file.type)
                  }}
                >
                  {getIcon(file.type)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {file.name}
                  </Typography>
                }
                secondary={
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 1 }}>
                    <Chip 
                      label={file.type} 
                      size="small" 
                      sx={{ 
                        height: 20, 
                        fontSize: '0.7rem',
                        bgcolor: alpha(getTypeColor(file.type), 0.1),
                        color: getTypeColor(file.type),
                        borderRadius: '4px'
                      }} 
                    />
                    <Typography variant="caption" color="text.secondary">
                      {file.date}
                    </Typography>
                  </Box>
                }
              />
              {showDelete && (
                <ListItemSecondaryAction>
                  <Tooltip title="Supprimer">
                    <IconButton 
                      edge="end" 
                      aria-label="supprimer"
                      onClick={() => handleDelete(file)}
                      sx={{ 
                        color: colors.redAccent[400],
                        '&:hover': { 
                          bgcolor: alpha(colors.redAccent[400], 0.1) 
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              )}
            </ListItem>
            {index < files.length - 1 && <Divider sx={{ opacity: 0.6 }} />}
          </React.Fragment>
        ))}
      </List>
    </Card>
  );
}